/**
 * Subtitle bubble anchored to Denker's cursor position.
 *
 * Always self-driven: when `text` changes, animates `revealed` from 0 →
 * text.length at 20ms/char.
 * No TTS dependency — the bubble is text-only.
 *
 * Spec: docs/specs/2026-04-30-desktop-onboarding-design.md §3.3
 */
import type React from "react";
import { useEffect, useState } from "react";
import { surfaceRoleAttributes } from "@/components/ui";
import { cursorGlassStyle } from "@/components/cursors/agent-cursor-glass";
import { requestNativeGlassRefresh } from "@/desktop/use-native-glass-surfaces";
import { cn } from "@/lib/cn";
import type { Position } from "./denker-cursor-controller-types";

const REVEAL_INTERVAL_MS = 20;
const SYNCED_REVEAL_INTERVAL_MS = 34;
const DENKER_COLOR = "#30D158";

interface Props {
  text: string | null;
  position: Position;
  /** Optional action UI rendered below the bubble copy, inside the same
   *  glass shell. Used by the cursor-led permissions flow to mount its
   *  per-state surface (Allow button / drag illustration / live transcript).
   *  When null, the bubble renders text only. */
  actionContent?: React.ReactNode | null;
  /** Optional second line rendered below the main text in a smaller,
   *  muted style. Useful for two-line bubble layouts. */
  hint?: string | null;
  syncWithSpeech?: boolean;
  /** Decorative instruction media rendered above the text. Never affects interactivity. */
  media?: React.ReactNode | null;
}

export function DenkerBubble({
  text,
  position,
  actionContent,
  hint,
  syncWithSpeech = false,
  media,
}: Props) {
  const [revealed, setRevealed] = useState("");
  const [opacity, setOpacity] = useState(0);
  const hasActionContent = Boolean(actionContent);
  const hasMedia = Boolean(media);
  const glassStyle = cursorGlassStyle(DENKER_COLOR);

  useEffect(() => {
    if (!text) {
      setRevealed("");
      setOpacity(0);
      return;
    }
    const nextText = text ?? "";
    setRevealed("");
    setOpacity(1);

    // Reveal char-by-char, then HOLD. The bubble persists while the
    // user takes the action — the next phase swaps `text` and that
    // re-runs this effect to reveal the new copy. No auto-fade.
    let interval: number | null = null;
    const startReveal = () => {
      if (interval !== null) return;
      let i = 0;
      interval = window.setInterval(
        () => {
          i += 1;
          setRevealed(nextText.slice(0, i));
          if (i >= nextText.length && interval !== null) {
            window.clearInterval(interval);
            interval = null;
          }
        },
        syncWithSpeech ? SYNCED_REVEAL_INTERVAL_MS : REVEAL_INTERVAL_MS,
      );
    };
    startReveal();
    return () => {
      if (interval !== null) window.clearInterval(interval);
    };
  }, [text, syncWithSpeech]);

  useEffect(() => {
    if (!text && !hasActionContent && !hasMedia) return;
    requestNativeGlassRefresh();
  }, [hasActionContent, revealed, text, hasMedia]);

  if (!text && !actionContent && !media) return null;

  return (
    <div
      data-testid="denker-bubble"
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })}
      // `data-interactive="true"` is the compact-window hit-test marker
      // (see use-click-through.ts). Permission bubbles need it for their
      // action buttons; text-only narration stays click-through.
      data-interactive={actionContent ? "true" : undefined}
      style={{
        ...glassStyle,
        // Anchored below the cursor's name label (AgentCursor renders the
        // "Denker" name at left:12, top:12 of the cursor; ~14px tall + 4px
        // gap → bubble starts at top:30). Same x-offset as the name label
        // so the unit reads as one stack, matching the spec mockup.
        position: "fixed",
        left: position.x + 12,
        top: position.y + 36,
        zIndex: 10000,
        opacity: !text && (hasActionContent || hasMedia) ? 1 : opacity,
        transition: "opacity 400ms ease-out",
      }}
      className={cn(
        // Onboarding context — slightly larger than canvas AgentCursor
        // (max-w-[280px], px-2.5 py-1.5) because it's the focal point. Fluid:
        // the bubble grows with longer translations (German) up to a readable
        // measure, and never past the window's edge.
        "min-w-[min(340px,calc(100vw-24px))] max-w-[min(520px,calc(100vw-24px))]",
        "denker-agent-cursor-bubble denker-onboarding-readable-bubble border",
        "px-appkit-md py-appkit-sm text-appkit-body leading-relaxed",
        // Click-target carve-out: the bubble itself receives clicks only when
        // its action surface is interactive in compact mode.
        actionContent ? "pointer-events-auto" : "pointer-events-none",
      )}
    >
      {media && (
        <div data-testid="denker-bubble-media" className="mb-1">
          {media}
        </div>
      )}
      {text && (
        // The full text is laid out invisibly so the bubble opens at its
        // final width/height; the streamed reveal overlays it. Without this
        // the bubble reflows on every revealed character and any action
        // surface below visibly resizes with it.
        <span className="relative block">
          <span aria-hidden="true" className="invisible">
            {text}
          </span>
          <span data-testid="denker-bubble-text" className="absolute inset-0">
            {revealed}
          </span>
        </span>
      )}
      {hint && (
        <p
          data-testid="denker-bubble-hint"
          className="mt-1 whitespace-pre-line text-appkit-caption text-[var(--agent-aurora-text-muted)]"
        >
          {hint}
        </p>
      )}
      {actionContent && (
        <div
          data-testid="denker-bubble-action"
          className={cn(text ? "mt-2" : "")}
        >
          {actionContent}
        </div>
      )}
    </div>
  );
}
